import { computed, onMounted, onUnmounted, ref, toValue, watch } from 'vue'
import type { MaybeRefOrGetter } from 'vue'
import { getApiDateTimeMillis } from '~/utils/getApiDateTimeMillis'

export function useOrderCountdown(
  deadline: MaybeRefOrGetter<string | null | undefined>,
  onExpire?: () => void
) {
  const remainingMs = ref<number | null>(null)
  let interval: ReturnType<typeof setInterval> | null = null

  const stop = () => {
    if (interval !== null) {
      clearInterval(interval)
      interval = null
    }
  }

  const tick = () => {
    const dl = toValue(deadline)
    if (dl == null || dl.length === 0) {
      remainingMs.value = null
      stop()
      return
    }
    const deadlineMs = getApiDateTimeMillis(dl)
    if (deadlineMs === null) {
      remainingMs.value = null
      stop()
      return
    }
    const remaining = Math.max(0, deadlineMs - Date.now())
    remainingMs.value = remaining
    if (remaining === 0) {
      stop()
      onExpire?.()
    }
  }

  const start = () => {
    stop()
    tick()
    if (remainingMs.value !== null && remainingMs.value > 0) {
      interval = setInterval(tick, 1000)
    }
  }

  onMounted(() => {
    const mountedDeadline = toValue(deadline)
    if (mountedDeadline != null && mountedDeadline.length > 0) start()
  })

  watch(
    () => toValue(deadline),
    (val) => {
      stop()
      if (val != null && val.length > 0) start()
      else remainingMs.value = null
    }
  )

  onUnmounted(stop)

  const label = computed((): string | null => {
    const ms = remainingMs.value
    if (ms === null) return null
    const totalSeconds = Math.floor(ms / 1000)
    const hours = Math.floor(totalSeconds / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const seconds = totalSeconds % 60
    if (hours > 0) {
      return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
    }
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
  })

  const isExpired = computed(() => remainingMs.value === 0)

  const isUrgent = computed(
    () => remainingMs.value !== null && remainingMs.value > 0 && remainingMs.value <= 2 * 60 * 1000
  )

  return { remainingMs, label, isExpired, isUrgent }
}
